import { variantInventory, variantLabel, type ShopifyVariant } from '@/lib/ordering';
import { cn } from '@/lib/cn';

// Variant table with per-size qty inputs, clamped to available inventory —
// shared by PlaceOrderDialog (per tier) and SampleOrderDialog.
export function VariantQtyRows({
  variants,
  qty,
  onChange,
}: {
  variants: ShopifyVariant[];
  qty: Record<string, number>;
  onChange: (variantId: string, qty: number) => void;
}) {
  if (!variants.length) {
    return (
      <div className="p-4 text-center text-[12px] text-muted">
        No variants found in Shopify — the product may have been deleted.
      </div>
    );
  }
  return (
    <div>
      <div className="grid grid-cols-[1fr_1fr_1fr_64px] gap-2 border-b border-border px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-subtle">
        <span>Size</span>
        <span>SKU</span>
        <span>Inventory</span>
        <span className="text-right">Qty</span>
      </div>
      {variants.map((v) => {
        const inventory = variantInventory(v);
        const tone = inventory > 10 ? 'text-success' : inventory > 0 ? 'text-warning' : 'text-danger';
        const label = inventory > 10 ? `${inventory} in stock` : inventory > 0 ? `${inventory} left` : 'Out of stock';
        const id = String(v.id);
        return (
          <div key={id} className="grid grid-cols-[1fr_1fr_1fr_64px] items-center gap-2 border-b border-border/50 px-2 py-1.5 text-[12px]">
            <span className="font-medium">{variantLabel(v)}</span>
            <span className="truncate font-mono text-[10.5px] text-muted">{v.sku || id}</span>
            <span className={cn('flex items-center gap-1.5 text-[11px]', tone)}>
              <span className={cn('inline-block h-1.5 w-1.5 rounded-full', inventory > 10 ? 'bg-success' : inventory > 0 ? 'bg-warning' : 'bg-danger')} />
              {label}
            </span>
            <input
              type="number"
              min={0}
              max={inventory}
              value={qty[id] ?? 0}
              disabled={inventory === 0}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10) || 0;
                onChange(id, Math.max(0, Math.min(n, inventory)));
              }}
              className="h-7 w-full rounded-sm border border-border bg-bg px-1.5 text-right text-[12px] focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-40"
            />
          </div>
        );
      })}
    </div>
  );
}
